import {
  DEFAULT_APP_NAME,
  DEFAULT_BRAND_INITIAL,
  DEFAULT_BRAND_ICON,
  DEFAULT_BRAND_LOGO,
  getBrandInitial,
  isDefaultBrandName,
} from '@/lib/default-brand';

type BrandVariant = 'primary' | 'sidebar';

interface DefaultBrandIconProps {
  initial?: string;
  variant?: BrandVariant;
  className?: string;
}

export function DefaultBrandIcon({
  initial = DEFAULT_BRAND_INITIAL,
  variant = 'primary',
  className = '',
}: DefaultBrandIconProps) {
  const isGestyFallback =
    !initial?.trim() ||
    initial.trim().toUpperCase() === DEFAULT_BRAND_INITIAL ||
    isDefaultBrandName(initial);

  if (isGestyFallback) {
    return (
      <img
        src={DEFAULT_BRAND_ICON}
        alt=""
        aria-hidden
        className={`size-8 shrink-0 rounded-[10px] object-contain ${className}`}
      />
    );
  }

  const colorClass =
    variant === 'sidebar'
      ? 'bg-sidebar-primary text-sidebar-primary-foreground'
      : 'bg-primary text-primary-foreground';

  return (
    <span
      aria-hidden
      className={`grid size-8 shrink-0 place-items-center rounded-[10px] font-bold tracking-tight shadow-[0_8px_20px_rgba(31,111,255,0.24)] ${colorClass} ${className}`}
    >
      {getBrandInitial(initial)}
    </span>
  );
}

interface DefaultBrandMarkProps {
  name?: string;
  initial?: string;
  showName?: boolean;
  variant?: BrandVariant;
  className?: string;
  iconClassName?: string;
  textClassName?: string;
}

export function DefaultBrandMark({
  name = DEFAULT_APP_NAME,
  initial,
  showName = true,
  variant = 'primary',
  className = '',
  iconClassName = '',
  textClassName = '',
}: DefaultBrandMarkProps) {
  const resolvedInitial = initial ?? name;
  const useGestyLogo = isDefaultBrandName(name);

  if (useGestyLogo) {
    return (
      <span className={`inline-flex min-w-0 items-center ${className}`}>
        <img
          src={DEFAULT_BRAND_LOGO}
          alt={DEFAULT_APP_NAME}
          className={`h-7 w-auto max-w-full object-contain ${textClassName}`}
        />
      </span>
    );
  }

  return (
    <span className={`inline-flex min-w-0 items-center gap-2 ${className}`}>
      <DefaultBrandIcon
        initial={resolvedInitial}
        variant={variant}
        className={iconClassName}
      />
      {showName && (
        <span
          className={`truncate text-[1.12rem] font-bold tracking-tight ${textClassName}`}
        >
          {name}
        </span>
      )}
    </span>
  );
}
